import React, { useState, useEffect } from "react";
import { Edit, Trash2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./CollectorList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const CollectorList = () => {
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const fetchCollectors = async () => {
    if (!token) return;
    
    try {
      console.log('Fetching collectors with token:', token?.substring(0, 20) + '...');
      const res = await axios.get(`${url}/api/collectors`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Collectors fetched:', res.data);
      setCollectors(res.data.data || []);
    } catch (err) {
      console.error('Error fetching collectors:', err);
      console.error('Error response:', err.response?.data);
      
      if (err.response?.status === 401) {
        // Unauthorized - token might be expired
        // Your auth context should handle this
      }
      
      // Use toast instead of alert for better UX
      const errorMsg = err.response?.data?.message || "Failed to fetch collectors";
      // You might want to use toast here if you have it set up
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && token) {
      fetchCollectors();
    }
  }, [token, authLoading]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this collector?")) {
      try {
        await axios.delete(`${url}/api/collectors/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Refresh the list
        fetchCollectors();
        alert("Collector deleted successfully");
      } catch (err) {
        console.error('Error deleting collector:', err);
        alert(err.response?.data?.message || "Failed to delete collector");
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch(status?.toLowerCase()) {
      case 'active':
        return 'status-badge active';
      case 'inactive':
        return 'status-badge inactive';
      default:
        return 'status-badge';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="collector-container">
        <div className="loading-spinner">Loading collectors...</div>
      </div>
    );
  }

  return (
    <div className="collector-container">
      <div className="header">
        <h2>Collectors List</h2>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          {user?.companyDetails && (
            <div style={{ 
              padding: '8px 15px', 
              background: '#e9ecef', 
              borderRadius: '20px',
              fontSize: '14px'
            }}>
              <span style={{ color: '#495057' }}>🏢 </span>
              <strong>{user.companyDetails.companyname}</strong>
              {user.companyDetails.acronym && ` (${user.companyDetails.acronym})`}
            </div>
          )}
          <button onClick={() => navigate("/AddCollector")} className="add-button">
            <Plus size={16} /> Add Collector
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="collector-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Full Name</th>
              <th>Zone</th>
              <th>District</th>
              <th>Mobile</th>
              <th>Username</th>
              <th>Status</th>
              <th>Created By</th>
              <th>Created At</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {collectors.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <p style={{ fontSize: '16px', color: '#6c757d' }}>No collectors found</p>
                    <button 
                      onClick={() => navigate("/AddCollector")} 
                      style={{ 
                        marginTop: '10px',
                        padding: '8px 20px',
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      Add Your First Collector
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              collectors.map((collector) => (
                <tr key={collector._id}>
                  <td>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={collector.photo ? `${url}/${collector.photo}` : '/default-avatar.png'}
                        alt={`${collector.fname} ${collector.lname}`}
                        width="50"
                        height="50"
                        style={{ 
                          borderRadius: "50%", 
                          objectFit: 'cover',
                          border: '2px solid #dee2e6'
                        }}
                        onError={(e) => {
                          e.target.src = '/default-avatar.png';
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '500' }}>
                      {[collector.fname, collector.mname, collector.lname]
                        .filter(Boolean)
                        .join(' ') || '—'}
                    </div>
                  </td>
                  <td>{collector.zonename?.name || '—'}</td>
                  <td>{collector.districtname?.name || '—'}</td>
                  <td>{collector.mobile || '—'}</td>
                  <td>
                    <code style={{ background: '#f8f9fa', padding: '2px 6px', borderRadius: '4px' }}>
                      {collector.username}
                    </code>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(collector.status)}>
                      {collector.status || 'inactive'}
                    </span>
                  </td>
                  <td>
                    {collector.createdBy ? (
                      <div>
                        <div style={{ fontWeight: '500' }}>{collector.createdBy.name}</div>
                        <div style={{ fontSize: '11px', color: '#6c757d' }}>{collector.createdBy.email}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    {collector.createdAt ? new Date(collector.createdAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="actions-cell">
                    <div className="actions">
                      <button
                        onClick={() => navigate(`/collectors/edit/${collector._id}`)}
                        className="edit-btn"
                        title="Edit Collector"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(collector._id)}
                        className="delete-btn"
                        title="Delete Collector"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Stats */}
      {collectors.length > 0 && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          background: '#f8f9fa', 
          borderRadius: '8px',
          display: 'flex',
          gap: '20px',
          justifyContent: 'space-around'
        }}>
          <div>
            <strong>Total Collectors:</strong> {collectors.length}
          </div>
          <div>
            <strong>Active:</strong> {collectors.filter(c => c.status === 'active').length}
          </div>
          <div>
            <strong>Inactive:</strong> {collectors.filter(c => c.status === 'inactive').length}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectorList;